import { z } from "zod";

/**
 * Wire-level response for POST /api/ai-responses/check.
 * The route returns this on success (HTTP 200) after starting the
 * Temporal workflow. The CLI uses workflowId to poll for status.
 */
export const ScanCheckResponse = z.object({
  workflowId: z.string(),
  status: z.literal("running"),
  message: z.string(),
  contextType: z.string(),
  projectId: z.string(),
  promptCount: z.number(),
  totalChecks: z.number(),
  totalRuns: z.number(),
});
export type ScanCheckResponse = z.infer<typeof ScanCheckResponse>;

/**
 * Wire-level response for GET /api/ai-responses/status/:workflowId.
 *
 * The route returns one of four shapes depending on workflow state:
 *
 *   "running"    — workflow in progress; `progress` is present.
 *   "completed"  — workflow finished; `result` with the enriched brand
 *                  visibility summary and per-prompt results is present.
 *   "failed"     — workflow threw; `error` explains the failure.
 *   <other>      — Temporal terminal states ("terminated", "cancelled",
 *                  "timed_out") also carry `status` + `error`.
 *
 * A flat schema covers all branches: `result` and `progress` are
 * optional and appear only in their respective status branches.
 * CLI commands check `status` before accessing `result` or `progress`.
 *
 * `result.summary.mentionRate` is an integer 0-100 (already
 * multiplied), matching the webapp's `Math.round(...)` output.
 * `result.summary.byEngine[engine].rate` is the same integer form.
 */
export const ScanStatusResponse = z.object({
  status: z.string(),
  contextType: z.string().nullable().optional(),
  projectId: z.string().nullable().optional(),
  contentId: z.string().nullable().optional(),
  error: z.string().optional(),
  progress: z
    .object({
      status: z.string().optional(),
      percentage: z.number().optional(),
      completedChecks: z.number().optional(),
      totalChecks: z.number().optional(),
      currentEngine: z.string().optional(),
      currentPrompt: z.string().optional(),
      mentionsFound: z.number().optional(),
    })
    .optional(),
  result: z
    .object({
      summary: z.object({
        totalMentions: z.number(),
        totalChecks: z.number(),
        mentionRate: z.number(),
        byEngine: z.record(
          z.object({
            checks: z.number(),
            mentions: z.number(),
            rate: z.number(),
          }),
        ),
      }),
      results: z.array(
        z.object({
          promptId: z.string(),
          promptText: z.string(),
          engine: z.string(),
          brandMentioned: z.boolean(),
          mentionContext: z.string().optional(),
          checkedAt: z.string(),
        }),
      ),
      availableEngines: z.array(z.string()),
      unavailableEngines: z.array(
        z.object({
          engine: z.string(),
          reason: z.string(),
        }),
      ),
      completedAt: z.string(),
    })
    .optional(),
});
export type ScanStatusResponse = z.infer<typeof ScanStatusResponse>;
